import React, { useMemo, useState } from 'react';

import { Alert, Col, Row, Spin, Table, Tag, Typography } from 'antd';

import { useQuery } from '@tanstack/react-query';

import { useNavigate } from 'react-router-dom';

import { useCurrency } from '@/contexts/CurrencyContext';

import {

  filtersQueryKey,

  getSalesAnalyticsOverview,

  operationsBridgeUrl,

  ownerIntelligencePresalesUrl,

  type SalesAnalyticsFilterParams,

} from '@/services/salesAnalytics.service';

import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';

import { OiAreaTrendChart, OiHorizontalBarChart } from '@/components/owner-intelligence/charts/OiCharts';

import { SalesAnalyticsToolbar } from './SalesAnalyticsToolbar';

import {

  SalesAnalyticsDrillDownDrawer,

  type SalesAnalyticsDrillDownRow,

} from './SalesAnalyticsDrillDownDrawer';



const { Title, Text } = Typography;



function comparisonExtra(

  cmp: { change_pct: number | null; change_abs: number } | undefined,

  formatCurrency: (n: number) => string

): React.ReactNode {

  if (!cmp || cmp.change_pct == null) return undefined;

  const positive = cmp.change_pct >= 0;

  return (

    <Text type={positive ? 'success' : 'danger'} style={{ fontSize: 12 }}>

      {positive ? '+' : ''}

      {cmp.change_pct}% ({formatCurrency(cmp.change_abs)})

    </Text>

  );

}



export const SalesAnalyticsOverviewTab: React.FC<{

  filters: SalesAnalyticsFilterParams;

  filterSummary: string;

}> = ({ filters, filterSummary }) => {

  const { formatCurrency } = useCurrency();

  const navigate = useNavigate();

  const [drillStage, setDrillStage] = useState<string | null>(null);



  const query = useQuery({

    queryKey: ['sales-analytics', 'overview', filtersQueryKey(filters)],

    queryFn: () => getSalesAnalyticsOverview(filters),

  });



  const data = query.data;

  const funnelChart = useMemo(

    () =>

      (data?.funnel ?? []).map((s) => ({

        label: s.stage.replace('Sales ', 'SO '),

        count: s.count,

        value: s.value,

        conversion: s.conversion_pct_from_prior != null ? `${s.conversion_pct_from_prior}%` : '—',

      })),

    [data?.funnel]

  );



  const trendData = useMemo(

    () => (data?.revenue_trend ?? []).map((p) => ({ label: p.monthLabel, revenue: p.revenue })),

    [data?.revenue_trend]

  );



  const drillRows: SalesAnalyticsDrillDownRow[] = useMemo(() => {

    if (!drillStage || !data) return [];

    const stage = data.funnel.find((s) => s.stage === drillStage);

    if (!stage) return [];

    return [

      {

        key: `${drillStage}-summary`,

        label: drillStage,

        sublabel: `${stage.count} documents · ${formatCurrency(stage.value)} total value`,

        amount: stage.value,

      },

    ];

  }, [drillStage, data, formatCurrency]);



  const printRows = funnelChart.map((r) => ({

    stage: r.label,

    count: r.count,

    value: r.value,

    conversion: r.conversion,

  }));



  if (query.isLoading) return <Spin />;

  if (query.isError) return <Alert type="error" message={(query.error as Error).message} />;



  const kpis = data!.kpis;

  const cmp = data!.kpi_comparison;

  const funnelLabel =

    data!.funnel_mode === 'cohort'

      ? 'Cohort funnel (leads created in period → receipt lineage)'

      : 'Volume funnel (documents in period)';



  return (

    <div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>

        <Title level={5} style={{ margin: 0 }}>

          Overview

        </Title>

        <SalesAnalyticsToolbar

          tab="overview"

          tabTitle="Overview"

          filters={filters}

          filterSummary={filterSummary}

          printColumns={[

            { key: 'stage', label: 'Stage' },

            { key: 'count', label: 'Count', align: 'right' },

            { key: 'value', label: 'Value', align: 'right' },

            { key: 'conversion', label: 'Conversion %', align: 'right' },

          ]}

          printRows={printRows}

          printKpiLines={[

            { label: 'Invoiced', value: formatCurrency(kpis.invoiced_revenue) },

            { label: 'Booked SO', value: formatCurrency(kpis.booked_so_value) },

            { label: 'Collections', value: formatCurrency(kpis.collections_in_period) },

          ]}

        />

      </div>



      {data!.executive_insights.length > 0 && (

        <Alert

          type="info"

          showIcon

          style={{ marginBottom: 16 }}

          message="Executive summary"

          description={

            <ul style={{ margin: 0, paddingLeft: 20 }}>

              {data!.executive_insights.map((ins, i) => (

                <li key={i}>

                  <Tag color={ins.level === 'positive' ? 'green' : ins.level === 'warning' ? 'orange' : 'blue'}>

                    {ins.level}

                  </Tag>{' '}

                  {ins.message}

                </li>

              ))}

            </ul>

          }

        />

      )}



      <Row gutter={[12, 12]}>

        <Col xs={24} sm={12} lg={8}>

          <ControlTowerMetricTile

            title="Invoiced revenue"

            count={kpis.invoice_count}

            amount={kpis.invoiced_revenue}

            formatCurrency={formatCurrency}

            extra={comparisonExtra(cmp?.invoiced_revenue, formatCurrency)}

            footerLink={{ label: 'View overdue AR →', onClick: () => navigate(operationsBridgeUrl('overdue-ar', filters)) }}

          />

        </Col>

        <Col xs={24} sm={12} lg={8}>

          <ControlTowerMetricTile

            title="Booked SO value"

            count={0}

            amount={kpis.booked_so_value}

            formatCurrency={formatCurrency}

            valueDisplay={formatCurrency(kpis.booked_so_value)}

            extra={comparisonExtra(cmp?.booked_so_value, formatCurrency)}

            footerLink={{ label: 'View orders at risk →', onClick: () => navigate(operationsBridgeUrl('orders-at-risk', filters)) }}

          />

        </Col>

        <Col xs={24} sm={12} lg={8}>

          <ControlTowerMetricTile

            title="Open pipeline"

            count={0}

            amount={kpis.open_pipeline_value}

            formatCurrency={formatCurrency}

            valueDisplay={formatCurrency(kpis.open_pipeline_value)}

            footerLink={{ label: 'Owner Intelligence pipeline →', onClick: () => navigate(ownerIntelligencePresalesUrl(filters)) }}

          />

        </Col>

        <Col xs={24} sm={12} lg={8}>

          <ControlTowerMetricTile

            title="Collections (period)"

            count={0}

            amount={kpis.collections_in_period}

            formatCurrency={formatCurrency}

            valueDisplay={formatCurrency(kpis.collections_in_period)}

            extra={comparisonExtra(cmp?.collections_in_period, formatCurrency)}

          />

        </Col>

        <Col xs={24} sm={12} lg={8}>

          <ControlTowerMetricTile title="Active customers" count={kpis.active_customers} valueDisplay={String(kpis.active_customers)} />

        </Col>

        <Col xs={24} sm={12} lg={8}>

          <ControlTowerMetricTile

            title="Open backlog"

            count={kpis.open_backlog_count}

            amount={kpis.open_backlog_value}

            formatCurrency={formatCurrency}

            footerLink={{ label: 'View deliveries →', onClick: () => navigate(operationsBridgeUrl('delivery-queues', filters)) }}

          />

        </Col>

      </Row>



      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>

        <Col xs={24} lg={12}>

          <Title level={5}>{funnelLabel}</Title>

          <Table

            size="small"

            pagination={false}

            rowKey="stage"

            dataSource={data!.funnel}

            onRow={(record) => ({

              onClick: () => setDrillStage(record.stage),

              style: { cursor: 'pointer' },

            })}

            columns={[

              { title: 'Stage', dataIndex: 'stage' },

              { title: 'Count', dataIndex: 'count', align: 'right' },

              {

                title: 'Value',

                dataIndex: 'value',

                align: 'right',

                render: (v: number) => formatCurrency(v ?? 0),

              },

              {

                title: 'Conv. %',

                dataIndex: 'conversion_pct_from_prior',

                align: 'right',

                render: (v: number | null) => (v != null ? `${v}%` : '—'),

              },

            ]}

          />

          <OiHorizontalBarChart data={funnelChart} dataKey="count" nameKey="label" height={220} />

        </Col>

        <Col xs={24} lg={12}>

          <Title level={5}>12-month revenue trend (filtered)</Title>

          <OiAreaTrendChart data={trendData} dataKey="revenue" xKey="label" height={280} />

          <Alert

            type="info"

            showIcon

            style={{ marginTop: 12 }}

            message="Operations bridge"

            description={

              <>

                KPI tiles link to Operations queues with shared team/customer context.{' '}

                <a onClick={() => navigate(operationsBridgeUrl('overdue-ar', filters))}>Overdue AR</a>

                {' · '}

                <a onClick={() => navigate(operationsBridgeUrl('delivery-queues', filters))}>Deliveries</a>

                {' · '}

                <a onClick={() => navigate('/growth/dashboard?tab=operations')}>All operations</a>

              </>

            }

          />

        </Col>

      </Row>



      <SalesAnalyticsDrillDownDrawer

        open={Boolean(drillStage)}

        onClose={() => setDrillStage(null)}

        title={drillStage ? `Funnel: ${drillStage}` : ''}

        subtitle={filterSummary}

        rows={drillRows}

        formatCurrency={formatCurrency}

      />

    </div>

  );

};


